/**
 * Price Slider Snippet
 *
 * Dual-handle draggable price range slider used inside the collection filter
 * modal (snippets/filter-group.liquid, price_range branch).
 *
 * The domain is derived from the collection's actual price range
 * (data-range-min / data-range-max, floor/ceil-aligned to data-step) so the
 * handles always map to real prices. Handles write their euro value to the
 * hidden `input[data-filter-price]` fields and dispatch a bubbling `change`
 * event so CollectionProducts.onFilterInputChange applies the filter.
 *
 * Uses Pointer Events (+ touch-action:none in CSS) so drag works with mouse,
 * touch and pen.
 *
 * Wired via data-snippet-script="price-slider" (utils/snippets.js).
 */
export default class PriceSlider {
  constructor(el) {
    // The filter modal is re-inserted/observed on swaps; guard against a
    // second init binding duplicate drag listeners.
    if (el.dataset.psInit === 'true') return;
    el.dataset.psInit = 'true';

    this.el = el;
    this.track = el.querySelector('[data-price-track]');
    this.fill = el.querySelector('[data-price-fill]');
    this.handles = {
      min: el.querySelector('[data-price-handle="min"]'),
      max: el.querySelector('[data-price-handle="max"]'),
    };
    this.displays = {
      min: el.querySelector('[data-price-display="min"]'),
      max: el.querySelector('[data-price-display="max"]'),
    };
    this.inputs = {
      min: el.querySelector('input[data-filter-price="min"]'),
      max: el.querySelector('input[data-filter-price="max"]'),
    };

    if (!this.track || !this.handles.min || !this.handles.max) return;

    this.step = Number(el.dataset.step) || 5;

    // Derive a step-aligned domain from the collection's real price range.
    const rangeMin = this.parseNum(el.dataset.rangeMin, 0);
    const rangeMax = this.parseNum(el.dataset.rangeMax, 100);
    this.min = Math.floor(rangeMin / this.step) * this.step;
    this.max = Math.ceil(rangeMax / this.step) * this.step;
    // Guarantee a usable, non-zero-width domain.
    if (this.max - this.min < this.step) this.max = this.min + this.step;

    // Active filter values (if any) come in as strings; empty means "at edge".
    const rawMin = (el.dataset.valueMin ?? '').trim();
    const rawMax = (el.dataset.valueMax ?? '').trim();
    this.values = {
      min: rawMin === '' ? this.min : this.clampToStep(this.parseNum(rawMin, this.min)),
      max: rawMax === '' ? this.max : this.clampToStep(this.parseNum(rawMax, this.max)),
    };

    // Ensure the initial values already keep the required gap.
    if (this.values.max - this.values.min < this.step) {
      this.values.min = Math.max(this.min, this.values.max - this.step);
    }

    this.render();
    // Normalise the hidden inputs to clean integers (or empty) without firing
    // a fetch, so composeFilterUrl always reads valid, locale-free values.
    this.writeInputs();
    this.bind();
  }

  // Locale-tolerant numeric parse: handles a decimal comma ("44,00").
  parseNum(str, fallback) {
    if (str == null) return fallback;
    const n = parseFloat(String(str).replace(',', '.'));
    return Number.isNaN(n) ? fallback : n;
  }

  clampToStep(v) {
    if (Number.isNaN(v)) return this.min;
    const clamped = Math.min(this.max, Math.max(this.min, v));
    return Math.round((clamped - this.min) / this.step) * this.step + this.min;
  }

  pct(v) {
    return ((v - this.min) / (this.max - this.min)) * 100;
  }

  render() {
    const minPct = this.pct(this.values.min);
    const maxPct = this.pct(this.values.max);
    this.handles.min.style.left = `${minPct}%`;
    this.handles.max.style.left = `${maxPct}%`;
    this.fill.style.left = `${minPct}%`;
    this.fill.style.width = `${maxPct - minPct}%`;
    this.displays.min.textContent = `€${this.values.min}`;
    this.displays.max.textContent = `€${this.values.max}`;
  }

  valueFromClientX(clientX) {
    const rect = this.track.getBoundingClientRect();
    const ratio = rect.width ? (clientX - rect.left) / rect.width : 0;
    const bounded = Math.min(1, Math.max(0, ratio));
    return this.clampToStep(this.min + bounded * (this.max - this.min));
  }

  setValue(which, v) {
    // Keep at least one step between the handles so the two dots never
    // overlap and neither can pass the other.
    if (which === 'min') {
      this.values.min = Math.max(this.min, Math.min(v, this.values.max - this.step));
    } else {
      this.values.max = Math.min(this.max, Math.max(v, this.values.min + this.step));
    }
    this.render();
  }

  bind() {
    ['min', 'max'].forEach((which) => {
      const handle = this.handles[which];

      handle.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        handle.setPointerCapture?.(e.pointerId);
        const move = (ev) => this.setValue(which, this.valueFromClientX(ev.clientX));
        const up = () => {
          handle.removeEventListener('pointermove', move);
          handle.removeEventListener('pointerup', up);
          handle.removeEventListener('pointercancel', up);
          this.commit();
        };
        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', up);
        handle.addEventListener('pointercancel', up);
      });

      handle.addEventListener('keydown', (e) => {
        let delta = 0;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = this.step;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -this.step;
        if (!delta) return;
        e.preventDefault();
        this.setValue(which, this.clampToStep(this.values[which] + delta));
        this.commit();
      });
    });
  }

  // Write current handle values into the hidden inputs. Active if either handle
  // moved off its domain edge — then emit BOTH bounds so the applied filter (and
  // its chip) reflect the two handle values rather than falling back to the
  // products' own price range. Otherwise clear both so an untouched slider adds
  // no filter.
  writeInputs() {
    const active = this.values.min > this.min || this.values.max < this.max;
    if (this.inputs.min) this.inputs.min.value = active ? String(this.values.min) : '';
    if (this.inputs.max) this.inputs.max.value = active ? String(this.values.max) : '';
  }

  commit() {
    this.writeInputs();
    // Both hidden inputs are read together by composeFilterUrl(); dispatch a
    // single change so only one fetch fires per interaction.
    const notify = this.inputs.min || this.inputs.max;
    notify?.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
